import React from 'react'
import Error from 'next/error'
import logBuildError from '@helpers/misc/logBuildError'
import { isProdEnv } from '@helpers/env'
import Head from '@components/Head'
import CollectionList from '@components/collection'
import NotFound from '../404'
import {
  getCollectionByHandle,
  getCollectionHandles
} from '../../server/api/graphqlContentService/collections'
import {
  Collection,
  CollectionConfig,
  CollectionHandles,
  CollectionListMeta
} from 'types/collection'
import { CustomError } from '@helpers/errors'
import { ProductsBreakdownProps } from '@components/ui/ProductsBreakdown'
import axios, { AxiosError } from 'axios'
import { FilterBuild } from '@components/collection/CollectionFilter/FiltersSidePanel/types'
import { getFilterBuildAdapter } from '@helpers/collections/adapter'
import { transformCollections } from '@helpers/collections/adapter/transformCollections'
import type { IncomingHttpHeaders } from 'http'

const handleCollectionsBuildError = logBuildError('collections')

export type CollectionsPageProps = {
  collectionData: {
    collections: Collection[]
    collectionConfig: CollectionConfig
    title: string
    byline: string
    description: CollectionConfig['detailed_description']
    meta: CollectionListMeta
  }
  collectionHero: {
    badge: {
      title: string
    }
  }
  productsBreakdown: ProductsBreakdownProps
  productIds: number[]
  productTypes: string[]
  collectionHandle: string
  statusCode: number
  headers: IncomingHttpHeaders | string
  filterBuild: FilterBuild
}

function CollectionsPage(props: CollectionsPageProps) {
  const { collectionData } = props

  if (props.statusCode === 404) return <NotFound />

  if (props.statusCode !== 200) {
    return <Error headers={props.headers} statusCode={props.statusCode} />
  }

  return (
    <>
      <Head
        title={collectionData.meta.seo_title || collectionData.title}
        image={collectionData.meta.seo_image}
        desc={
          collectionData.meta.seo_description ||
          `Spoke ${collectionData.title} Collection`
        }
        includeNoFollow={false}
      />
      <CollectionList {...props} />
    </>
  )
}

function collectionHandlesManager() {
  let handles: CollectionHandles

  return {
    get: async function () {
      if (!handles) {
        handles = (await getCollectionHandles()) || []
      }

      return handles
    }
  }
}
const handlesManager = collectionHandlesManager()
let buildContainsErrors = false

export async function getStaticPaths() {
  const collectionHandles = await handlesManager.get()

  const paths = collectionHandles.map(collection => ({
    params: { collectionHandle: collection.handle }
  }))

  return { paths, fallback: false }
}

type CollectionPageProps = {
  params: {
    collectionHandle: string
  }
}

export async function getStaticProps({ params }: CollectionPageProps) {
  const { collectionHandle } = params
  try {
    const collectionHandles = await handlesManager.get()
    const lastCollectionHandle = collectionHandles?.at(-1)?.handle

    if (collectionHandle === lastCollectionHandle && buildContainsErrors) {
      throw new CustomError('Build contains errors')
    }

    const collectionHandleData = collectionHandles?.find(
      collection => collection.handle === collectionHandle
    )
    const groupTag = collectionHandleData?.group_tag || ''
    const pageData = await getCollectionByHandle(collectionHandle, groupTag)

    if ('errors' in pageData) {
      buildContainsErrors = true
      throw new CustomError(
        `API error - ON.stringify for collectionHandle '${collectionHandle}' and groupTag '${groupTag}' with errors ${JSON.stringify(
          pageData.errors
        )} }`
      )
    }

    if (!pageData.collections.length) {
      handleCollectionsBuildError({
        errorLevel: 'warning',
        properties: {
          handle: collectionHandle,
          message: 'Empty collection'
        }
      })
    }

    if (!pageData.collectionConfig) {
      handleCollectionsBuildError({
        errorLevel: 'warning',
        properties: {
          handle: collectionHandle,
          message: 'Collection missing config data'
        }
      })
    }

    const filteredCollections = pageData.collections.filter(
      collection => !!collection.items?.length
    )

    const productTypes = filteredCollections.map(
      collection => collection.product_type
    )
    const productIds = filteredCollections.flatMap(collection =>
      collection.items.map(item => item.id)
    )

    const transformedCollections = transformCollections(filteredCollections)

    const filterBuild = getFilterBuildAdapter(transformedCollections)

    const filterBuildHasMultipleCategoryTypes = filterBuild.length > 1

    const collectionData = {
      title: pageData.title,
      byline: pageData.byline,
      description: pageData.collectionConfig?.detailed_description,
      collections: transformedCollections,
      meta: {
        header: pageData.title,
        seo_title: pageData.collectionConfig?.seo_title,
        seo_description: pageData.collectionConfig?.seo_description,
        seo_image: pageData.collectionConfig?.seo_image,
        show_product_type:
          pageData.collectionConfig?.show_product_type_on_each_product || false,
        filters: {
          show: filterBuild.length > 0,
          multi: filterBuildHasMultipleCategoryTypes
        },
        page_crawl: pageData.collectionConfig?.page_crawl || false
      }
    }

    return {
      props: {
        filterBuild,
        collectionHandle,
        collectionData,
        href: `/collections/${collectionHandle}`,
        productIds,
        productTypes,
        statusCode: 200
      }
    }
  } catch (e) {
    const error = e as typeof CustomError | AxiosError
    const handle = collectionHandle

    console.error(error)
    if (
      (error instanceof CustomError && error.status === 404) ||
      (axios.isAxiosError(error) && error.response?.status === 404)
    ) {
      handleCollectionsBuildError({
        errorLevel: 'warning',
        properties: {
          handle,
          message: '404 - Collection does not exist'
        }
      })
    } else {
      const errorMessage =
        (error instanceof CustomError && error.message) ||
        (axios.isAxiosError(error) && error?.message)
      handleCollectionsBuildError({
        errorLevel: 'critical',
        properties: {
          handle,
          ...(axios.isAxiosError(error) && error.response
            ? {
                path: error.request.path,
                error: error.response.data || error.response.statusText
              }
            : { error: errorMessage })
        }
      })
      if (
        process.env.NODE_ENV === 'production' &&
        isProdEnv() &&
        errorMessage === 'Build contains errors'
      ) {
        throw new CustomError('Collection page failed to export')
      }
    }

    return {
      notFound: true,
      props: {
        headers:
          axios.isAxiosError(error) && error.response
            ? error.response.headers
            : '',
        statusCode:
          axios.isAxiosError(error) && error.response
            ? error.response.status
            : 404
      }
    }
  }
}

export default CollectionsPage
